import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, MapPin, Clock, Star, Navigation, Phone, Route } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { isOrderTrackingUser } from '@/config/features';
import RatingModal from '@/components/RatingModal';
import OrderCancelButton from '@/components/OrderCancelButton';
import BottomNavigation from '@/components/BottomNavigation';

interface OrderItem {
  quantity: number;
  price: number;
  name?: string;
  menu_items?: {
    name: string;
  } | null;
}

interface Order {
  id: string;
  restaurant_id: string;
  driver_id: string | null;
  status: string;
  total_amount: number;
  delivery_fee: number;
  created_at: string;
  completed_at: string | null;
  delivery_address: string;
  delivery_type: string;
  notes: string | null;
  order_items: OrderItem[];
  restaurants: {
    id: string;
    name: string;
    address: string;
  };
  ratings?: {
    id: string;
    restaurant_rating: number | null;
    driver_rating: number | null;
  }[];
}

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { isAdmin } = useIsAdmin();
  const canTrack = isOrderTrackingUser(user?.email, isAdmin);
  const [orders, setOrders] = useState<Order[]>([]);
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [ratingModal, setRatingModal] = useState<{
    isOpen: boolean;
    orderId: string;
    restaurantId: string;
    driverId: string;
    customerId: string;
    restaurantName?: string;
    restaurantImage?: string | null;
    items?: { name: string; quantity: number }[];
  }>({
    isOpen: false,
    orderId: '',
    restaurantId: '',
    driverId: '',
    customerId: '',
  });
  const [driverPhones, setDriverPhones] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (user) {
      fetchOrders();
      
      // Set up ENHANCED real-time subscription for order status updates
      const channel = supabase
        .channel('customer-orders-enhanced')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            filter: `customer_id=eq.${user.id}`
          },
          (payload) => {
            console.log('=== CUSTOMER ORDER UPDATE DETECTED ===');
            console.log('Event Type:', payload.eventType);
            console.log('Order Data:', payload.new);
            
            // Show toast notification for status changes with FIXED DURATION
            if (payload.eventType === 'UPDATE' && payload.new) {
              const order = payload.new;
              
              if (order.status === 'confirmed') {
                toast({
                  title: "Order Confirmed!",
                  description: "Your order has been confirmed by the restaurant.",
                  duration: 4000,
                });
              } else if (order.status === 'ready_for_pickup') {
                if (order.delivery_type === 'pickup') {
                  toast({
                    title: "Order Ready!",
                    description: "Your order is ready for pickup.",
                    duration: 4000,
                  });
                } else {
                  toast({
                    title: "Order Ready!",
                    description: "Your order is ready and waiting for driver pickup.",
                    duration: 4000,
                  });
                }
              } else if (order.status === 'picked_up') {
                if (order.delivery_type === 'delivery') {
                  toast({
                    title: "Driver Picked Up Your Order!",
                    description: "Your order is on the way to you.",
                    duration: 4000,
                  });
                }
              } else if (order.status === 'delivered') {
                toast({
                  title: "Order Delivered!",
                  description: "Your order has been delivered successfully.",
                  duration: 4000,
                });
              }
            }
            
            // CRITICAL FIX: Refresh orders immediately after showing toast with small delay
            setTimeout(() => {
              fetchOrders();
            }, 500); // Small delay to ensure database consistency
          }
        )
        .subscribe((status) => {
          console.log('Customer order subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to customer order updates');
          }
        });

      return () => {
        console.log('Cleaning up customer order subscription');
        supabase.removeChannel(channel);
      };
    }
  }, [user, toast]);

  const cancelOrder = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;

      toast({
        title: "Order Cancelled",
        description: "Your order has been cancelled successfully.",
        duration: 4000,
      });

      // Refresh orders after cancellation
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast({
        title: "Error",
        description: "Failed to cancel order. Please try again.",
        duration: 4000,
      });
    }
  };

  const fetchDriverPhone = async (driverId: string) => {
    if (driverPhones[driverId]) return driverPhones[driverId];
    
    try {
      const { data, error } = await supabase
        .from('driver_applications')
        .select('phone_number')
        .eq('user_id', driverId)
        .single();

      if (error || !data) {
        // Try profiles table as fallback
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('phone')
          .eq('id', driverId)
          .single();
        
        if (!profileError && profileData?.phone) {
          setDriverPhones(prev => ({ ...prev, [driverId]: profileData.phone }));
          return profileData.phone;
        }
        return null;
      }

      setDriverPhones(prev => ({ ...prev, [driverId]: data.phone_number }));
      return data.phone_number;
    } catch (error) {
      console.error('Error fetching driver phone:', error);
      return null;
    }
  };

  const fetchOrders = async () => {
    if (!user) return;

    try {
      console.log('=== FETCHING CUSTOMER ORDERS ===');
      console.log('Customer ID:', user.id);
      
      // Only fetch orders that actually exist (have been created after successful payment or cash orders)
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            quantity,
            price,
            name,
            menu_items (
              name
            )
          ),
          restaurants (
            id,
            name,
            address
          ),
          ratings (
            id,
            restaurant_rating,
            driver_rating
          )
        `)
        .eq('customer_id', user.id)
        .not('id', 'is', null) // Ensure we only get real orders
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }

      console.log('Raw order data:', data);

      // Cast the data to ensure proper typing - convert ratings to array format
      const typedData = data?.map(order => ({
        ...order,
        ratings: Array.isArray(order.ratings) ? order.ratings : (order.ratings ? [order.ratings] : [])
      })) as Order[];

      // Fetch driver phone numbers for active delivery orders
      const activeDeliveryOrders = typedData?.filter(order => 
        order.delivery_type === 'delivery' && 
        order.driver_id && 
        order.status === 'picked_up'
      ) || [];

      for (const order of activeDeliveryOrders) {
        if (order.driver_id) {
          await fetchDriverPhone(order.driver_id);
        }
      }

      // Separate active and completed orders based on proper status flow
      const active = typedData?.filter(order => {
        // Active orders are those that are still in progress
        if (order.delivery_type === 'pickup') {
          return ['pending', 'confirmed', 'ready_for_pickup'].includes(order.status);
        } else {
          // For delivery orders, include picked_up as active (driver is delivering)
          return ['pending', 'confirmed', 'ready_for_pickup', 'picked_up'].includes(order.status);
        }
      }) || [];

      const completed = typedData?.filter(order => {
        // Completed orders have finished or been cancelled
        if (order.delivery_type === 'pickup') {
          return ['picked_up', 'delivered', 'cancelled'].includes(order.status);
        } else {
          return ['delivered', 'cancelled'].includes(order.status);
        }
      }) || [];

      console.log('Active orders:', active);
      console.log('Completed orders:', completed);

      setActiveOrders(active);
      setOrders(completed);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Error",
        description: "Failed to load orders. Please try again.",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string, deliveryType: string) => {
    if (status === 'picked_up' && deliveryType === 'pickup') {
      return 'bg-green-100 text-green-800';
    }
    
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'ready_for_pickup':
        return 'bg-purple-100 text-purple-800';
      case 'picked_up':
        return 'bg-orange-100 text-orange-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string, deliveryType: string) => {
    if (status === 'picked_up' && deliveryType === 'pickup') {
      return 'COMPLETED';
    }
    if (status === 'picked_up' && deliveryType === 'delivery') {
      return 'ON THE WAY';
    }
    if (status === 'ready_for_pickup' && deliveryType === 'pickup') {
      return 'READY FOR PICKUP';
    }
    if (status === 'ready_for_pickup' && deliveryType === 'delivery') {
      return 'READY FOR DRIVER';
    }
    return status.replace('_', ' ').toUpperCase();
  };

  const canRate = (order: Order) => {
    const isCompleted = order.status === 'delivered' || (order.status === 'picked_up' && order.delivery_type === 'pickup');
    const hasNotRated = !order.ratings || order.ratings.length === 0;
    return isCompleted && hasNotRated;
  };

  const handleRate = (order: Order) => {
    setRatingModal({
      isOpen: true,
      orderId: order.id,
      restaurantId: order.restaurant_id,
      driverId: order.driver_id || '',
      customerId: user?.id || '',
      restaurantName: order.restaurants?.name,
      restaurantImage: (order.restaurants as any)?.image_url ?? null,
      items: (order.order_items || []).map((i) => ({
        name: i.name || i.menu_items?.name || 'Item',
        quantity: i.quantity,
      })),
    });
  };

  const handleNavigateToRestaurant = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  const handleCallDriver = (driverId: string) => {
    const driverPhone = driverPhones[driverId];
    if (driverPhone) {
      window.location.href = `tel:${driverPhone}`;
    }
  };

  const OrderCard = ({ order, showRating = false }: { order: Order; showRating?: boolean }) => {
    const isMarketplaceOrder = !order.restaurant_id;
    
    return (
    <Card key={order.id} className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {isMarketplaceOrder ? 'Marketplace Order' : order.restaurants?.name || 'Restaurant Order'}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status, order.delivery_type)}`}>
              {getStatusText(order.status, order.delivery_type)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{new Date(order.created_at).toLocaleDateString()}</span>
          </div>
          <span>Order #{order.id.slice(0, 8)}</span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div>
          <h4 className="font-medium mb-2">Items:</h4>
          <div className="space-y-1">
            {order.order_items.map((item, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span>{item.quantity}x {item.name || item.menu_items?.name || 'Item'}</span>
                <span>D{item.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              {order.delivery_type === 'pickup' ? 'Pickup Location:' : 'Delivery Address:'}
            </p>
            <p className="text-sm text-gray-600">
              {order.delivery_type === 'pickup' && !isMarketplaceOrder 
                ? order.restaurants?.address 
                : order.delivery_address}
            </p>
            {order.delivery_type === 'pickup' && !isMarketplaceOrder && order.restaurants?.address && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNavigateToRestaurant(order.restaurants.address)}
                className="mt-2 text-blue-600 border-blue-600 hover:bg-blue-50"
              >
                <Navigation className="h-3 w-3 mr-1" />
                Navigate to Restaurant
              </Button>
            )}
          </div>
        </div>

        {/* Driver contact info for active delivery orders */}
        {order.delivery_type === 'delivery' && 
         order.driver_id && 
         order.status === 'picked_up' && 
         driverPhones[order.driver_id] && (
          <div className="bg-blue-50 p-3 rounded-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Your Driver</p>
                <p className="text-sm text-blue-600">{driverPhones[order.driver_id]}</p>
              </div>
              <Button
                onClick={() => handleCallDriver(order.driver_id!)}
                variant="outline"
                size="sm"
                className="text-green-600 border-green-600 hover:bg-green-50"
              >
                <Phone className="h-4 w-4 mr-1" />
                Call
              </Button>
            </div>
          </div>
        )}

        {order.notes && (
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm font-medium mb-1">Notes:</p>
            <p className="text-sm text-gray-600">{order.notes}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t">
          <div>
            <p className="text-lg font-semibold">Total: D{order.total_amount.toFixed(2)}</p>
            {order.delivery_fee > 0 && (
              <p className="text-sm text-gray-600">
                (includes D{order.delivery_fee.toFixed(2)} delivery fee)
              </p>
            )}
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {canTrack && !showRating && ['confirmed','preparing','ready','ready_for_pickup','picked_up'].includes(order.status) && (
              <Button
                onClick={() => navigate(`/track-order/${order.id}`)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Route className="h-4 w-4 mr-1" />
                Track Order
              </Button>
            )}
            {/* Show cancel button only for pending orders */}
            {order.status === 'pending' && (
              <OrderCancelButton
                orderId={order.id}
                onCancel={() => cancelOrder(order.id)}
                orderStatus={order.status}
              />
            )}
            
            {showRating && canRate(order) && (
              <Button
                onClick={() => handleRate(order)}
                variant="outline"
                className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
              >
                <Star className="h-4 w-4 mr-1" />
                Rate Order
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b border-gray-200 px-4 py-5">
        <h1 className="text-2xl font-bold">My Orders</h1>
      </div>

      <div className="p-4">
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-white text-red-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Active Orders ({activeOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'bg-white text-red-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Completed ({orders.length})
          </button>
        </div>

        {activeTab === 'active' && (
          <div>
            {activeOrders.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Active Orders</h3>
                  <p className="text-gray-600">Your current orders will appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {activeOrders.map(order => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'completed' && (
          <div>
            {orders.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Order History</h3>
                  <p className="text-gray-600">Your completed orders will appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <OrderCard key={order.id} order={order} showRating={true} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <RatingModal
        isOpen={ratingModal.isOpen}
        orderId={ratingModal.orderId}
        restaurantId={ratingModal.restaurantId}
        driverId={ratingModal.driverId}
        customerId={ratingModal.customerId}
        restaurantName={ratingModal.restaurantName}
        restaurantImage={ratingModal.restaurantImage}
        items={ratingModal.items}
        onClose={() => {
          setRatingModal({ isOpen: false, orderId: '', restaurantId: '', driverId: '', customerId: '' });
          fetchOrders();
        }}
      />

      <BottomNavigation />
    </div>
  );
};

export default MyOrdersPage;
